/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Popover } from "@mui/material";

import {
  EmptyLabel,
  MeasurementItem,
  MeasurementLayer,
  OverflowBadge,
  PopoverContent,
  TagListContainer,
} from "./styles";

const TAG_GAP = 8;
const OVERFLOW_BADGE_WIDTH = 40;

// Lays out tags in a fixed-width cell, moving whatever does not fit into a hover popover
// behind a +N badge. The tag markup itself belongs to the caller.
export const OverflowTagList = React.memo(
  ({
    data = [],
    renderTag,
    emptyLabel,
    testIdPrefix,
    overflowAriaLabel,
  }) => {
    const containerRef = useRef(null);
    const measurementRefs = useRef(new Map());
    const hoverTimeout = useRef(null);

    const [visibleItems, setVisibleItems] = useState([]);
    const [hiddenItems, setHiddenItems] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const calculateVisibleItems = useCallback(() => {
      if (!containerRef.current || !data.length) return;

      const containerWidth = containerRef.current.offsetWidth;

      if (!containerWidth) {
        setVisibleItems([]);
        setHiddenItems(data);
        return;
      }

      let visibleCount = 0;
      let usedWidth = 0;

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const measuredElement = measurementRefs.current.get(item.id);

        if (!measuredElement) {
          return;
        }

        const itemWidth = Math.ceil(measuredElement.getBoundingClientRect().width);
        const gapWidth = visibleCount > 0 ? TAG_GAP : 0;
        const remainingItems = data.length - (i + 1);
        const overflowWidth = remainingItems > 0 ? TAG_GAP + OVERFLOW_BADGE_WIDTH : 0;
        const requiredWidth = usedWidth + gapWidth + itemWidth + overflowWidth;

        if (requiredWidth > containerWidth) {
          break;
        }

        usedWidth += gapWidth + itemWidth;
        visibleCount = i + 1;
      }

      setVisibleItems(data.slice(0, visibleCount));
      setHiddenItems(data.slice(visibleCount));
    }, [data]);

    useEffect(() => {
      calculateVisibleItems();

      if (!containerRef.current) return;

      const observer = new ResizeObserver(() => {
        calculateVisibleItems();
      });

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
        clearTimeout(hoverTimeout.current);
      };
    }, [calculateVisibleItems]);

    const handleOpenPopover = (event) => {
      if (!hiddenItems.length) return;
      clearTimeout(hoverTimeout.current);
      setAnchorEl(event.currentTarget);
      setIsPopoverOpen(true);
    };

    const handleClosePopover = () => {
      hoverTimeout.current = setTimeout(() => {
        setAnchorEl(null);
        setIsPopoverOpen(false);
      }, 150);
    };

    const open = Boolean(anchorEl) && isPopoverOpen;

    if (!data.length) {
      return (
        <EmptyLabel data-testid={`${testIdPrefix}-empty-label`}>
          {emptyLabel}
        </EmptyLabel>
      );
    }

    return (
      <>
        <TagListContainer
          ref={containerRef}
          data-testid={`${testIdPrefix}-container`}
        >
          <MeasurementLayer aria-hidden>
            {data.map((item) => (
              <MeasurementItem
                key={`measurement-${item.id}`}
                ref={(element) => {
                  if (element) {
                    measurementRefs.current.set(item.id, element);
                  } else {
                    measurementRefs.current.delete(item.id);
                  }
                }}
              >
                {renderTag(item, { isMeasurement: true })}
              </MeasurementItem>
            ))}
          </MeasurementLayer>

          {visibleItems.map((item) => (
            <React.Fragment key={item.id}>
              {renderTag(item, { isMeasurement: false })}
            </React.Fragment>
          ))}

          {hiddenItems.length > 0 && (
            <OverflowBadge
              onMouseEnter={handleOpenPopover}
              data-testid={`${testIdPrefix}-overflow-badge`}
              aria-label={overflowAriaLabel?.(hiddenItems.length)}
            >
              +{hiddenItems.length}
            </OverflowBadge>
          )}
        </TagListContainer>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          disableRestoreFocus
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              onMouseEnter: () => clearTimeout(hoverTimeout.current),
              onMouseLeave: handleClosePopover,
              sx: { p: 2, borderRadius: 2 },
              "data-testid": `${testIdPrefix}-popover-paper`,
            },
          }}
        >
          <PopoverContent data-testid={`${testIdPrefix}-popover-content`}>
            {hiddenItems.map((item) => (
              <React.Fragment key={item.id}>
                {renderTag(item, { isMeasurement: false })}
              </React.Fragment>
            ))}
          </PopoverContent>
        </Popover>
      </>
    );
  },
);

OverflowTagList.displayName = "OverflowTagList";
